import React, { useEffect, useState } from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { IEpxPreDiabetes } from '../../../../../libs/apps/epxprediabetes';
import { DirectCell, YesNoCell, NAGuard, CellFunction } from '../../cells';
import Button from '../../../../molecules/button';
import Dialog from '../../../../organisms/dialog';
import Table from '../../../../../components/organisms/table';
import { CardFrame, CardTitle } from '../../../../organisms/card';
import { FormatDate } from '../../../../../libs/time';

const INTAKE_QUESTIONS = {
  continueNutritionPrompts: 'Continued With Nutrition Prompts',
  mealsPerDay: 'Meals Per Day',
  vegetableGrainFrequency: 'Vegetable/Grain Frequency',
  consumesTheSugary: 'Consumes Sugary snacks or drinks',
  hasHealthyFoodAccess: 'Has Access to healthy food',
  nutritionConnect: 'Wants to connect on nutrition',
  continueExercisePrompts: 'Continued With Exercise Prompts',
  physicalActivityPerWeek: 'Physical Activity Per Week',
  activityType: 'Activity Type',
  activityBarriers: 'Activty Barriers',
  healthCoachExerciseConnect: 'Wants to connect on exercise',
  continueWeightPrompts: 'Continued With Weight Prompts',
  hasWeightGoal: 'Has weight goal',
  weightGoalConfidence: 'Weight Goal Confidence',
  wantsWeightPlan: 'Wants a weight plan',
  continueWeightConversation:
    'Continued With weight prompts after weight plan question',
  trackingWeightorFood: 'Tacking weight or food',
  wantsTrackingTips: 'Wants tracking tips',
  weightLossBarriers: 'Weight loss barriers',
  wantsWeightCoachTimeConnect: 'Wants to connect on weight loss time barriers',
  wantsWeightCoachMotivationConnect:
    'Wants to connect on weight loss motivation barriers',
  wantsWeightCoachAccessConnect:
    'Wants to connect on weight loss healthy food access barriers',
  weightQuantity: 'Weight Quantity'
};

const isLabNACheck = () => {
  return (session: IEpxPreDiabetes): boolean => {
    return session.isLab === true;
  };
};

const isNotLabNACheck = () => {
  return (session: IEpxPreDiabetes): boolean => {
    return session.isLab === false;
  };
};

const EpxPreDiabetesTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const [intakeData, setIntakeData] = useState(null);
  const [transformedIntakeData, setTransformedIntakeData] = useState(null);
  const [intakeDateTime, setIntakeDateTime] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (intakeData) {
      const rows = [];
      Object.keys(INTAKE_QUESTIONS).forEach((key) => {
        rows.push({ question: INTAKE_QUESTIONS[key], answer: intakeData[key] });
      });
      setTransformedIntakeData(rows);
      const intakeTime = {
        respondedAt: intakeData.respondedAt,
        sentAt: intakeData.time
      };
      setIntakeDateTime(intakeTime);
    }
  }, [intakeData]);
  const columns: Column<IEpxPreDiabetes>[] = [
    {
      Header: 'Has Labs',
      id: 'hasLabs',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('hasLabs') as CellFunction<IEpxPreDiabetes>),
        isNotLabNACheck()
      )
    },
    {
      Header: 'Healthy Eating',
      id: 'healthyEating',
      Cell: NAGuard(
        ResponseGuard(
          DirectCell('healthyEating') as CellFunction<IEpxPreDiabetes>
        ),
        isLabNACheck()
      )
    },
    {
      Header: 'Physical Activity',
      id: 'physicalActivity',
      Cell: NAGuard(
        ResponseGuard(
          DirectCell('physicalActivity') as CellFunction<IEpxPreDiabetes>
        ),
        isLabNACheck()
      )
    },
    {
      Header: 'Weight Confidence',
      id: 'weightConfidence',
      Cell: NAGuard(
        ResponseGuard(
          DirectCell('weightConfidence') as CellFunction<IEpxPreDiabetes>
        ),
        isLabNACheck()
      )
    }
  ];

  const intakeColumns: Column<any>[] = [
    {
      id: 'intakeQuestion',
      Header: 'Question',
      Cell: ({ row: { original } }) => {
        const question = original?.question;
        return <b>{question}</b>;
      }
    },
    {
      id: 'intakeAnswer',
      Header: 'Answer',
      Cell: ({ row: { original } }) => {
        let answer = original?.answer;
        if (answer === true) {
          answer = 'Yes';
        } else if (answer === false) {
          answer = 'No';
        } else if (answer == null) {
          answer = 'Not Answered Yet';
        }
        return <span>{answer}</span>;
      }
    }
  ];

  return (
    <>
      <Dialog
        style={{
          paddingBottom: 0
        }}
        isOpen={open}
        onClose={() => setOpen(false)}
      >
        <CardFrame>
          <CardTitle>
            <h4>Intake Questionnaire Responses</h4>
            {intakeDateTime?.sentAt ? (
              <>
                <h4>
                  Responded At:{' '}
                  {intakeDateTime?.respondedAt
                    ? FormatDate(
                        intakeDateTime?.respondedAt,
                        'MM/DD/YYYY h:mma zz'
                      )
                    : 'No Response'}
                </h4>
                <h4>
                  Message Time:{' '}
                  {FormatDate(intakeDateTime?.sentAt, 'MM/DD/YYYY h:mma zz')}
                </h4>
              </>
            ) : null}
          </CardTitle>
          <Table<any>
            columns={intakeColumns}
            data={transformedIntakeData || []}
            bordered
            striped
          />
        </CardFrame>
      </Dialog>
      <Button className="mb-6" onClick={() => setOpen(true)}>
        View Intake Data
      </Button>
      <SessionTable<IEpxPreDiabetes>
        url="/epxprediabetes/data/"
        columns={columns}
        setIntakeData={setIntakeData}
        filterOutIntake
        {...props}
      />
    </>
  );
};

export default EpxPreDiabetesTable;
